import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getPublicPath } from 'utils/paths';

const CursorContainer = styled.div.attrs<{ $x: number; $y: number }>(
  ({ $x, $y }) => ({
    style: {
      transform: `translate3d(${$x - 22}px, ${$y}px, 0)`
    }
  })
)<{ $x: number; $y: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  pointer-events: none;
  z-index: 2147483647;
  will-change: transform;

  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const CursorImage = styled.div<{ $isPointer: boolean }>`
  width: 100%;
  height: 100%;
  background-image: ${(props) =>
    `url(${getPublicPath(
      props.$isPointer
        ? '/images/fredcon26/fredcon-cursor-pointer.png'
        : '/images/fredcon26/fredcon-cursor.png'
    )})`};
  background-size: contain;
  background-repeat: no-repeat;
`;

export const Fredcon26Cursor: React.FC = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateCursor = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // If we are over an iframe or Cookiebot dialog, hide the custom cursor and let the default cursor take over
      if (
        target &&
        (target.tagName === 'IFRAME' ||
          target.closest('#CybotCookiebotDialog, #CookiebotSessionDialog'))
      ) {
        setIsVisible(false);
        return;
      }

      setPosition({ x: e.clientX, y: e.clientY });
      if (!isVisible) setIsVisible(true);

      const isClickable =
        target &&
        target.closest(
          'a, button, [role="button"], .cursor-pointer, select, label'
        ) !== null;
      setIsPointer(isClickable);
    };

    const onMouseLeave = () => setIsVisible(false);
    const onMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', updateCursor, { passive: true });
    window.addEventListener('mouseover', updateCursor, { passive: true });
    document.body.addEventListener('mouseleave', onMouseLeave);
    document.body.addEventListener('mouseenter', onMouseEnter);

    return () => {
      window.removeEventListener('mousemove', updateCursor);
      window.removeEventListener('mouseover', updateCursor);
      document.body.removeEventListener('mouseleave', onMouseLeave);
      document.body.removeEventListener('mouseenter', onMouseEnter);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <CursorContainer $x={position.x} $y={position.y}>
      <CursorImage $isPointer={isPointer} />
    </CursorContainer>
  );
};
